import { useState } from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import { Link, useNavigate } from 'react-router-dom'
import { useFormik } from 'formik'
import { requestPassword, requestVerifyAndChangePassword } from '../core/_requests'
import VisibilityBox from '../../apps/common/visibility-box/VisibilityBox'
import { notify } from '../../../../utils/shared'
import logo from '../../../../_metronic/assets/logo/logo.svg';
import useTranslate from '../../../hooks/useTranslate'

const initialValues = {
  email: '',
  otp: '',
  password: ""
}

export function ForgotPassword() {
  const t= useTranslate();
  const [loading, setLoading] = useState(false);
  const forgotPasswordSchema = Yup.object().shape({
    email: Yup.string().email(t("INVALID_EMAIL_ADDRESS")).required(t("EMAIL_IS_REQUIRED")),
    otp: Yup.string()
      .min(6, t("MINIMUM_SIX_CHARACTERS"))
      .max(6, t("MAXIMUM_SIX_CHARACTERS"))
  })
  const [otpSended, setOtpSended] = useState(false);
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined);
  const navigate = useNavigate();
  const formik = useFormik({
    initialValues,
    validationSchema: forgotPasswordSchema,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      setLoading(true)
      setHasErrors(undefined)
      setTimeout(() => {
        if (otpSended) {
          requestVerifyAndChangePassword(values.email, values.otp, values.password)
            .then(({ data: { result } }) => {
              setHasErrors(false)
              setLoading(false);
              setOtpSended(false);
              notify(t("PASSWORD_CHANGED_SUCCESSFULLY"), t("SUCCESS"));
              navigate('/auth/login');
            })
            .catch(() => {
              setHasErrors(true)
              setLoading(false)
              setSubmitting(false)
              setStatus(t("LOGIN_DETAIL_IS_INCORRECT"))
            })
        } else {
          requestPassword(values.email)
            .then(({ data: { result } }) => {
              setHasErrors(false)
              setLoading(false);
              setOtpSended(true);
            })
            .catch(() => {
              setHasErrors(true)
              setLoading(false)
              setSubmitting(false)
              setStatus(t("LOGIN_DETAIL_IS_INCORRECT"))
            })
        }
      }, 1000)
    },
  })

  return (
    <form
      className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
      noValidate
      id='kt_login_password_reset_form'
      onSubmit={formik.handleSubmit}
    >
      <div className="fv-row mb-10 text-center">
        <img className="w-50 h-50" src={logo} alt="logo" />
      </div>
      <div className='text-center mb-10'>
        {/* begin::Title */}
        <h1 className='text-dark fw-bolder mb-3'>{t("FORGOT_PASSWORD")}</h1>
        {/* end::Title */}

        {/* begin::Link */}
        <div className='text-gray-500 fw-semibold fs-6'>
          {t("ENTER_YOUR_EMAIL_ADDRESS_TO_RESET_YOUR_PASSWORD")}  
        </div>
        {/* end::Link */}
      </div>

      {/* begin::Title */}
      {hasErrors === true && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>
            {t("WE_ARE_SORRY_BUT_WE_SEEM_TO_HAVE_ENCOUNTERED_ERRORS_PLEASE_TRY_AGAIN")}
          </div>
        </div>
      )}

      {hasErrors === false && (
        <div className='mb-10 bg-light-info p-8 rounded'>
          <div className='text-info'>
              {t("SUBMIT_THE_OTP_YOU_RECEIVED_VIA_EMAIL_TO_CONTINUE")}
            </div>
        </div>
      )}
      {/* end::Title */}

      {/* begin::Form group */}
      <div className='fv-row mb-8'>
        <label className='form-label fw-bolder text-gray-900 fs-6'>{t("EMAIL")}</label>
        <input
          type='email'
          placeholder={t("ENTER_EMAIL_ADDRESS")}
          autoComplete='off'
          readOnly={otpSended}
          {...formik.getFieldProps('email')}
          className={clsx(
            'form-control bg-transparent',
            { 'is-invalid': formik.touched.email && formik.errors.email },
            {
              'is-valid': formik.touched.email && !formik.errors.email,
            }
          )}
        />
        {formik.touched.email && formik.errors.email && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.email}</span>
            </div>
          </div>
        )}
      </div>
      <VisibilityBox show={otpSended}>
        <div className='fv-row mb-8'>
          <label className='form-label fw-bolder text-gray-900 fs-6'>{t("OTP")}</label>
          <input
            type='string'
            placeholder={t("ENTER_OTP")}
            autoComplete='off'
            {...formik.getFieldProps('otp')}
            className={clsx(
              'form-control bg-transparent',
              { 'is-invalid': formik.touched.otp && formik.errors.otp },
              {
                'is-valid': formik.touched.otp && !formik.errors.otp,
              }
            )}
          />
          {formik.touched.otp && formik.errors.otp && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.otp}</span>
              </div>
            </div>
          )}
        </div>
        <div className="fv-row mb-3">
          <label className="form-label fw-bolder text-dark fs-6 mb-0">
            {t("PASSWORD")}
          </label>
          <div className="position-relative">
            <input
              placeholder={t("PASSWORD")}
              type={"password"}
              autoComplete="off"
              {...formik.getFieldProps("password")}
              className={clsx(
                "form-control bg-transparent",
                {
                  "is-invalid": formik.touched.password && formik.errors.password,
                },
                {
                  "is-valid": formik.touched.password && !formik.errors.password,
                }
              )}
              name="password"
            />
          </div>
          {formik.touched.password && formik.errors.password && (
            <div className="fv-plugins-message-container">
              <div className="fv-help-block">
                <span role="alert">{formik.errors.password}</span>
              </div>
            </div>
          )}
        </div>
      </VisibilityBox>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
        <button type='submit' id='kt_password_reset_submit' className='btn btn-primary me-4'>
          <span className='indicator-label'> {t("SUBMIT")}</span>
          {loading && (
            <span className='indicator-progress'>
              {t("PLEASE_WAIT")}...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
        <Link to='/auth/login'>
          <button
            type='button'
            id='kt_login_password_reset_form_cancel_button'
            className='btn btn-light'
            disabled={formik.isSubmitting || !formik.isValid}
          >
           {t("LOGIN")}
          </button>
        </Link>{' '}
      </div>
      {/* end::Form group */}
    </form>
  )
}
